import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LoginForm from "../components/LoginForm";
import logo from "../assets/logo192.png";

const features = [
  { icon: "🔍", title: "Smart Search", text: "Describe what you want, in your own words." },
  { icon: "🎯", title: "Sharp Recommendations", text: "Suggestions that actually get better over time." },
  { icon: "📋", title: "Your Own Top Lists", text: "Rank and organize the games you care about." },
];

export default function SigninPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const message = location.state?.message;

  React.useEffect(() => {
    const poppinsId = "poppins-font";
    if (!document.getElementById(poppinsId)) {
      const link = document.createElement("link");
      link.id = poppinsId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const handleLogin = () => {
    navigate("/");
  };

  return (
    <div className="relative min-h-[calc(100vh-var(--header-height))] w-full flex items-stretch">
      {/* Left — brand showcase, hidden on small screens */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden items-center px-12 xl:px-16 py-16">
        <img
          src="https://images.unsplash.com/photo-1643489069237-3548135218c8?auto=format&fit=crop&w=1600&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-brand-gradient-135 opacity-70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-br from-surface/55 via-surface/35 to-surface/85" />
        <div className="absolute inset-0 gg-grid-backdrop opacity-60" />
        <div className="absolute -top-24 -left-16 w-80 h-80 rounded-full bg-brand-purple/30 blur-3xl animate-pulseGlow" />
        <div className="absolute -bottom-24 -right-10 w-72 h-72 rounded-full bg-brand-gold/20 blur-3xl animate-floaty" />

        {/* brand lockup */}
        <Link to="/" className="group absolute top-8 left-12 xl:left-16 z-10 flex items-center gap-2.5">
          <img
            src={logo}
            alt="Game Goblin"
            className="h-10 w-10 object-contain drop-shadow-[0_2px_10px_rgba(151,1,251,0.5)] transition-transform duration-300 group-hover:scale-105"
          />
          <span className="font-heading font-extrabold text-lg text-white">
            Game<span className="gg-gradient-text">Goblin</span>
          </span>
        </Link>

        <div className="relative z-10 max-w-md animate-fadeUp">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold text-brand-gold-soft backdrop-blur-sm mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
            The smart game search engine
          </span>
          <h1 className="font-heading font-extrabold text-white text-4xl xl:text-5xl leading-[1.1] mb-4">
            Find your next
            <br />
            <span className="gg-gradient-text">favourite game.</span>
          </h1>
          <p className="text-white/75 text-base xl:text-lg mb-9 leading-relaxed">
            Game Goblin is a search engine built for gamers. Describe what you're
            after and let the recommendations do the rest.
          </p>

          <div className="space-y-4">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-2xl bg-white/[0.07] border border-white/15 shadow-inset-line flex items-center justify-center text-xl">
                  {f.icon}
                </div>
                <div>
                  <p className="text-white font-semibold">{f.title}</p>
                  <p className="text-white/65 text-sm">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-12">
        <div className="absolute inset-0 gg-grid-backdrop opacity-[0.15] pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 h-64 w-64 rounded-full bg-brand-purple/15 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-[420px] animate-fadeUp">
          {/* mobile brand */}
          <Link to="/" className="lg:hidden flex items-center justify-center gap-2.5 mb-8">
            <img src={logo} alt="Game Goblin" className="h-10 w-10 object-contain drop-shadow-[0_2px_10px_rgba(151,1,251,0.5)]" />
            <span className="font-heading font-extrabold text-lg text-white">
              Game<span className="gg-gradient-text">Goblin</span>
            </span>
          </Link>

          <div className="flex flex-col items-center lg:items-start mb-7">
            <div className="h-14 w-14 rounded-2xl bg-brand-gradient flex items-center justify-center text-2xl shadow-glow-purple mb-4">
              🔑
            </div>
            <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl text-center lg:text-left">
              Welcome back
            </h2>
            <p className="text-white/55 text-sm mt-1.5 text-center lg:text-left">
              Sign in to keep your favourites, blacklist, and recommendations in sync.
            </p>
          </div>

          {message && (
            <div className="mb-5 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm font-medium px-4 py-3">
              {message}
            </div>
          )}

          <div className="gg-neon-ring rounded-3xl border border-white/10 bg-surface-card/55 backdrop-blur-xl shadow-card shadow-inset-line p-5 sm:p-7">
            <LoginForm onLogin={handleLogin} />
          </div>

          <p className="text-center text-white/55 text-sm mt-6">
            New to Game Goblin?{" "}
            <Link to="/auth/signup" className="text-brand-gold font-semibold hover:underline">
              Create an account
            </Link>
          </p>
          <p className="text-center mt-3">
            <Link to="/" className="text-white/40 text-xs hover:text-white/70 transition-colors">
              ← Back to home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
